import type { FloraAnnotation } from '../../containers/adherence/risk-analysis/risk-analysis.client.type';
import type { Board, ProjectsResponse } from '../projects-client/projects-client.type';
import type { Epic, MinifiedTask } from '../tasks-client/task-client.type';

enum RelativeBusinessValueUnits {
  numeric = 'numeric',
  dollars = 'dollars',
  none = 'none',
}

enum InitiativeStatus {
  OnTrack = 'on_track',
  AtRisk = 'at_risk',
  OffTrack = 'off_track',
  Unknown = 'unknown',
  Empty = 'empty',
}

enum InitiativeSource {
  Bloomfilter = 'bloomfilter',
  Jira = 'jira',
  JDC = 'jira_data_center',
  Github = 'github',
  ADO = 'ado',
}

enum InitiativeSourceStatus {
  Created = 'created',
  Updated = 'updated',
  Deleted = 'deleted',
}

enum InitiativeTasksFilters {
  Completed = 'completed',
  CompletedMonthly = 'completed_monthly',
  Discrete = 'discrete',
  Total = 'total',
  Epic = 'epic',
  Project = 'project',
  Active = 'active',
}

type Metric = {
  tasks: Record<string, number | null>;
  points: Record<string, number | null>;
};

type InitiativeStatusData = {
  by_tasks: {
    status: InitiativeStatus;
    percent_time_elapsed: number;
    percent_subtasks_complete: number;
  };
  by_points: {
    status: InitiativeStatus;
    percent_time_elapsed: number;
    percent_subtasks_complete: number;
  };
};

type Initiative = {
  id: string;
  key: string | null;
  name: string;
  url: string | null;
  description: string;
  source: InitiativeSource;
  source_status: InitiativeSourceStatus | null;
  start_date?: string;
  effective_start_date?: string;
  end_date: string;
  effective_end_date?: string;
  relative_business_value?: number | null;
  relative_business_value_unit?: RelativeBusinessValueUnits;
  portfolios: string[];
  portfolio: string;
  no_end_date: boolean;
  epic_count?: number;
  epic_ids?: string[];
  is_active: boolean;
  is_bookmarked: boolean;
  status?: InitiativeStatusData;
  percent_elapsed: number;
  percent_progress: number;
  custom_fields?: Record<string, string>;
  message?: string;
  costs_to_date?: CostsToDate;
  flora_annotations?: FloraAnnotation | null;
  contributing_teams_over_time?: Record<string, Metric>;
  contributing_boards_over_time?: Record<string, Metric>;
  focus_by_team_over_time?: Record<string, Metric>;
  project_names_map?: Record<string, string>;
  work_committed_by_project_over_time?: Record<string, Metric>;
  work_completed_by_project_over_time?: Record<string, Metric>;
  total_subtasks_over_time: Metric;
  completed_subtasks_over_time: Metric;
};

type InitialContributions = {
  team: {
    [id: string]: string | null;
  };
  board: {
    [id: string]: string | null;
  };
};

type CostsToDate = {
  initiative: number;
  epics: {
    [id: string]: number;
  };
  teams: {
    [id: string]: number;
  };
};

type ExtendedInitiative = Initiative & {
  url: string | null;
  projects: ProjectsResponse[];
  subprojects: Board[];
  epics: Epic[];
  total_subtasks_over_time: Metric;
  completed_subtasks_over_time: Metric;
  contributing_epics_over_time: Record<string, Metric>;
  contributing_teams_over_time: Record<string, Metric>;
  initial_contributions: InitialContributions;
  costs_to_date: CostsToDate;
  metadata_created_at: string;
  metadata_created_by: string | null;
  metadata_updated_at: string;
  metadata_modified_by: string | null;
  contains_active_epics_after_close: boolean;
};

type FocusData = Record<string, Metric>;

type PreSaveInitiative = {
  id?: string;
  name: string;
  description: string;
  source: InitiativeSource;
  start_date?: string | Date;
  end_date: string | Date;
  is_bookmarked?: boolean;
  relative_business_value?: number | null;
  relative_business_value_unit?: RelativeBusinessValueUnits;
  portfolios: string[];
  projects?: string[];
  epics?: string[];
};

type InitiativeTasksConfig = {
  portfolioId: string | undefined;
  initiativeId: string | undefined;
  filter: InitiativeTasksFilters | null;
  date: string | null;
  epicId: string | null;
  projectId: string | null;
};

type InitiativeTaskResponse = {
  name: string;
  tasks: MinifiedTask[];
};

type InitiativesByStatusDataPoint = {
  value: number;
  initiatives: string[];
};

type InitiativesByStatusData = {
  at_risk: InitiativesByStatusDataPoint;
  off_track: InitiativesByStatusDataPoint;
  on_track: InitiativesByStatusDataPoint;
  unknown: InitiativesByStatusDataPoint;
};

type InitiativeBreakdownDataPoint = {
  key: string | null;
  percentage: number;
};

type KPIOverviewData = {
  initiatives_by_status: {
    by_points: InitiativeBreakdownDataPoint[];
    by_tasks: InitiativesByStatusData;
  };
  overall_completion: number;
  work_completion: {
    completion_by_points: number;
    completion_by_tasks: number;
    total_tasks: number;
  };
  throughput: number;
  initiative_cost_breakdown: Record<string, InitiativeBreakdownDataPoint>;
  initiative_effort_breakdown: Record<string, InitiativeBreakdownDataPoint>;
};

export type {
  CostsToDate,
  ExtendedInitiative,
  FocusData,
  Initiative,
  InitiativeBreakdownDataPoint,
  InitiativeStatusData,
  InitiativeTaskResponse,
  InitiativeTasksConfig,
  KPIOverviewData,
  Metric,
  PreSaveInitiative,
};

export { InitiativeSource, InitiativeStatus, InitiativeTasksFilters, RelativeBusinessValueUnits };
